<template>
    <v-container :style="[containerStyle, sidebarStyles]" fluid class="bordered-container">
        <v-col cols="12">
            <component-wrapper :config="component" />
        </v-col>
    </v-container>
</template>

<script>
import components from '../../component.js'
import { isHomepageFixHorizontalScrollEnabled } from '../../featureFlags'
export default {
    name: 'zoezi-resource-availability-wrapper',
    zoezi: {
        title: 'Resource Availability',
        icon: 'mdi-table-row',
    },
    props: {
        component: {
            title: 'Component',
            type: Object,
            fulltype: 'component',
            default: () => components.makeEmptyConfig(),
        },
        selectedResource: {
            title: 'Resource',
            type: Number,
            fulltype: 'select',
            typeinfo: {
                multiple: false,
                text: 'name',
                value: 'id',
                getItems: () => {
                    return window.$zoeziapi.get('/api/resource/get').then((resources) => {
                        return resources.map(({ lastname, id }) => ({ name: lastname, id }));
                    });
                },
            },
            default: () => undefined,
        },
        soonOcupiedTime: {
            title: 'Pre-warning time before occupancy, in minutes',
            type: Number,
            default: 15,
        },
        freeColor: {
            title: 'Free Color',
            type: String,
            fulltype: 'color',
            default: 'success',
        },
        soonOcupiedColor: {
            title: 'Soon to be occupied Color',
            type: String,
            fulltype: 'color',
            default: 'warning',
        },
        ocupiedColor: {
            title: 'Occupied Color',
            type: String,
            fulltype: 'color',
            default: 'error',
        },
        barWidth: {
            title: 'Bar width',
            type: String,
            fulltype: 'csssize',
            default: '30px', // Default width
        },
        fullScreen: {
            title: 'Show in fullscreen',
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        bookings: undefined, // Default undefined, null if no bookings
        unsubscribe: null,
        dailyFetch: null,
    }),
    watch: {
        selectedResource: {
            handler: function (val) {
                this.fetchBookings(val);
            },
            immediate: true,
        },
    },
    computed: {
        containerStyle() {
            let style = {};
            if (this.fullScreen) {
                style.height = '100vh';
                style.width = this.homepageFixHorizontalScrollEnabled ? '100%' : '100vw';
            }
            return style || null;
        },
        isOccupied() {
            return !!this.bookings?.find((booking) => {
                if (booking.start <= this.$store.state.now30s && this.$store.state.now30s <= booking.end) {
                    return true;
                }
            });
        },
        soonOcupied() {
            let date = this.$store.state.now30s.clone();
            return !!this.bookings?.find((booking) => {
                if (date.addMinutes(this.soonOcupiedTime) >= booking.start && booking.end > this.$store.state.now30s) {
                    return true;
                }
            });
        },
        sidebarStyles() {
            let barColor = '#aaaaaa'; // Default color, no resource selected or no bookings fetched
            if (this.selectedResource && this.bookings !== undefined) {
                if (this.isOccupied) {
                    barColor = this.ocupiedColor;
                } else if (this.soonOcupied) {
                    barColor = this.soonOcupiedColor;
                } else {
                    barColor = this.freeColor;
                }
            }
            return {
                '--bar-width': this.barWidth,
                '--bar-color': this.$root.colorToHex(barColor)
            };
        },
        homepageFixHorizontalScrollEnabled() {
            return isHomepageFixHorizontalScrollEnabled(this);
        }
    },
    methods: {
        fetchBookings(resource_id) {
            if (resource_id) {
                let today = new Date();
                let tomorrow = new Date().addDays(1);

                this.$api.post('/api/resourcebooking/resource/get_resource_allocation', {
                    mode: 'slots',
                    resource_ids: [resource_id],
                    start: { '$datetime': today.yyyymmddSpacehhmmss() },
                    end: { '$datetime': tomorrow.yyyymmddSpacehhmmss() },
                }).then(async (res) => {
                    if (res.ok) {
                        const bookings = await res.json()

                        // Fix dates from string to Date
                        this.bookings = bookings.map((booking) => {
                            booking.start = Date.newFull(booking.start.$datetime);
                            booking.end = Date.newFull(booking.end.$datetime);
                            return booking;
                        });
                    } else {
                        this.bookings = undefined;
                    }
                })
            }
        },
    },
    mounted() {
        this.unsubscribe = this.$root.$api.subscribe({type: 'resourceallocation', callback: (booking) => {
            if (booking.resource_id == this.selectedResource) {
                this.fetchBookings(this.selectedResource);
            }
        }});

        // Maintain the bookings updated if open for a long time
        this.dailyFetch = setInterval(() => {
            this.fetchBookings(this.selectedResource);
        }, 23*60*60*1000); // Fetch every day, 23 hours for margin
    },
    destroyed() {
        if (this.unsubscribe) { this.unsubscribe(); }
        if (this.dailyFetch) { clearInterval(this.dailyFetch); }
    }
};
</script>

<style scoped>
    .bordered-container {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        position: relative;
        padding: 0 calc(var(--bar-width));
    }

    .bordered-container::before,
    .bordered-container::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        width: var(--bar-width);
        background-color: var(--bar-color);
    }

    .bordered-container::before {
        left: 0;
    }

    .bordered-container::after {
        right: 0;
    }
</style>
